import {getLFCData, setLFCDataProc} from './LFCUtil';

export type AccumContext = Pick<Parameters<typeof setLFCDataProc>[0], 'snack' | 't'>;
export type AccumProfile = {
  id: string;
  priority: number;
  device_pattern: string;
  path_pattern: string;
  handling: 'git' | 'accumulate' | 'snapshot';
  record_format: 'block' | 'line' | 'keyvalue' | null;
  record_delimiter: string | null;
  key_pattern: string | null;
  header_pattern: string | null;
  retention_days: number | null;
  enabled: boolean;
};
export type ProfileDocument = {
  schema_version: number;
  company_id: string;
  profile_version: number;
  profiles: AccumProfile[];
};
export type AccumFile = {
  device: string;
  file_path: string;
  record_count: number;
  last_collected_at: string;
};
export type AccumRecord = {
  id: number;
  device: string;
  file_path: string;
  record_key: string | null;
  body: string;
  occurred_at: string;
  collected_at: string;
  object_id: string;
};
export type AccumObject = {
  id: number;
  object_id: string;
  device: string;
  file_path: string;
  collected_at: string;
  size_bytes: number;
  parse_status: string;
  record_count: number;
};
export type AccumQuery = {
  device: string;
  file_path: string;
  start_at: string;
  end_at: string;
  key_filter: string;
};

const query = <T>(ctx: AccumContext, sql_id: number, parameters: object): Promise<T[]> =>
  getLFCData({...ctx, sql_id, parameters, rejectEmptyTruncation: true});

/** カーソルで続きを取るクエリの1ページの件数 */
export const PAGE_LIMIT = 200;
/** 1ページ分の行と、続きのページがあるか */
export type Page<T> = {rows: T[]; hasMore: boolean};
/**
 * 1ページを取得する。API のサイズ上限で打ち切られたか、件数が上限に達したときだけ続きがあるとみなす
 * （上限未満で打ち切りもなければ最後のページ）。
 */
export const queryPage = async <T>(
  ctx: AccumContext,
  sql_id: number,
  parameters: object
): Promise<Page<T>> => {
  let truncated = false;
  const rows: T[] = await getLFCData({
    ...ctx,
    sql_id,
    parameters: {...parameters, limit: PAGE_LIMIT},
    rejectEmptyTruncation: true,
    onTruncated: () => {
      truncated = true;
    }
  });
  return {rows, hasMore: truncated || rows.length >= PAGE_LIMIT};
};
export const getAccumFiles = (ctx: AccumContext, device: string) =>
  query<AccumFile>(ctx, 8021, {device});
export const getAccumRecords = (ctx: AccumContext, parameters: AccumQuery, after_id = 0) =>
  queryPage<AccumRecord>(ctx, 8022, {...parameters, after_id});
export const getAccumObjects = (ctx: AccumContext, parameters: AccumQuery, after_id = 0) =>
  queryPage<AccumObject>(ctx, 8023, {...parameters, after_id});
const proc = async <T>(ctx: AccumContext, parameters: object): Promise<T> => {
  const rows = await setLFCDataProc({...ctx, invoke_name: 'PgHistoryAccum', parameters});
  if (!rows?.[0]) throw new Error('Empty accumulation response');
  return rows[0];
};
export const listProfiles = (ctx: AccumContext) =>
  proc<ProfileDocument>(ctx, {action: 'list_profiles'});
export const saveProfiles = (ctx: AccumContext, profiles: AccumProfile[]) =>
  proc<ProfileDocument>(ctx, {action: 'save_profiles', profiles});
export const getOriginalUrl = (ctx: AccumContext, object_id: string) =>
  proc<{url: string; file_name: string}>(ctx, {action: 'get_original_url', object_id});

/** 直近 `days` 日の期間を datetime-local 入力の形式（ブラウザーのタイムゾーン）で返す */
export function defaultPeriod(now = new Date(), days = 7): [string, string] {
  const local = (d: Date) =>
    new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
  const start = new Date(now);
  start.setDate(start.getDate() - days);
  return [local(start), local(now)];
}

export function csvCell(value: unknown): string {
  let text = String(value ?? '');
  if (/^[\s\u0000-\u001f]*[=+@-]/.test(text) || /^[\t\r\n]/.test(text)) text = "'" + text;
  return '"' + text.replaceAll('"', '""') + '"';
}

export async function collectAllPages<T extends {id: number}>(
  fetchPage: (after: number) => Promise<Page<T>>,
  cancelled = () => false
): Promise<T[]> {
  const all: T[] = [];
  let after = 0;
  for (;;) {
    if (cancelled()) throw new Error('Cancelled');
    const page = await fetchPage(after);
    if (cancelled()) throw new Error('Cancelled');
    for (const row of page.rows) {
      if (!Number.isSafeInteger(row.id) || row.id <= after)
        throw new Error('Invalid accumulation cursor');
      after = row.id;
      all.push(row);
    }
    if (!page.hasMore || !page.rows.length) return all;
  }
}

export const newProfile = (): AccumProfile => ({
  id: crypto.randomUUID(),
  priority: 100,
  device_pattern: '*',
  path_pattern: '',
  handling: 'accumulate',
  record_format: 'line',
  record_delimiter: null,
  key_pattern: null,
  header_pattern: null,
  retention_days: null,
  enabled: true
});
export type InvalidProfile = {id: string; field: keyof AccumProfile};
export function findInvalidProfile(profiles: AccumProfile[]): InvalidProfile | null {
  const ids = new Set<string>();
  for (const p of profiles) {
    if (!p.device_pattern.trim()) return {id: p.id, field: 'device_pattern'};
    if (!p.path_pattern.trim()) return {id: p.id, field: 'path_pattern'};
    if (!Number.isSafeInteger(p.priority)) return {id: p.id, field: 'priority'};
    if (ids.has(p.id)) return {id: p.id, field: 'id'};
    if (
      p.retention_days !== null &&
      (!Number.isSafeInteger(p.retention_days) || p.retention_days <= 0)
    )
      return {id: p.id, field: 'retention_days'};
    if (p.handling === 'snapshot' && p.retention_days !== null && p.retention_days !== 7)
      return {id: p.id, field: 'retention_days'};
    if (p.handling === 'accumulate' && p.record_format === 'block' && !p.record_delimiter?.trim())
      return {id: p.id, field: 'record_delimiter'};
    if (p.handling === 'accumulate' && p.record_format === 'keyvalue' && !p.key_pattern?.trim())
      return {id: p.id, field: 'key_pattern'};
    ids.add(p.id);
  }
  return null;
}
export function validateProfiles(profiles: AccumProfile[]): string | null {
  return findInvalidProfile(profiles) ? 'accum.invalidProfile' : null;
}
